import { useState, useCallback, useEffect, useRef, ImgHTMLAttributes } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import { DarkSkeletonTheme, LightSkeletonTheme } from "./skeletons/shared";
import cn from "classnames";

interface ClickableImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  fullscreenClassName?: string;
  light?: boolean;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

const clampPan = (panX: number, panY: number, currentZoom: number) => {
  if (currentZoom <= 1) return { x: 0, y: 0 };
  const vw = window.innerWidth * 0.9;
  const vh = window.innerHeight * 0.9;
  // Allow panning up to half the visible area so the image edge stays visible
  const maxX = (vw * (currentZoom - 1)) / 2;
  const maxY = (vh * (currentZoom - 1)) / 2;
  return {
    x: Math.max(-maxX, Math.min(maxX, panX)),
    y: Math.max(-maxY, Math.min(maxY, panY)),
  };
};

const ClickableImage = ({
  fullscreenClassName,
  className,
  src,
  alt,
  light = false,
  loading = "lazy",
  width,
  height,
  ...rest
}: ClickableImageProps) => {
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const lastPan = useRef({ x: 0, y: 0 });
  const initialPinchDist = useRef<number | null>(null);
  const pinchZoomStart = useRef(1);
  const overlayRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });

  zoomRef.current = zoom;
  panRef.current = pan;

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const close = useCallback(() => {
    setOpen(false);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    setZoom(z => {
      const next = Math.min(z + ZOOM_STEP, MAX_ZOOM);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setZoom(z => {
      const next = Math.max(z - ZOOM_STEP, MIN_ZOOM);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "+" || e.key === "=") zoomIn();
      if (e.key === "-") zoomOut();
      if (e.key === "0") resetZoom();
    };
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    document.body.style.pointerEvents = "none";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
      document.body.style.pointerEvents = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close, zoomIn, zoomOut, resetZoom]);

  // Native touch on overlay: block carousel + pinch-zoom & pan
  useEffect(() => {
    if (!open) return;
    const el = overlayRef.current;
    if (!el) return;

    const isInsideControls = (target: EventTarget | null) => {
      return controlsRef.current && target instanceof Node && controlsRef.current.contains(target);
    };

    const getTouchDist = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const onTouchStart = (e: TouchEvent) => {
      e.stopPropagation();
      if (isInsideControls(e.target)) return;
      if (e.touches.length === 2) {
        e.preventDefault();
        initialPinchDist.current = getTouchDist(e.touches);
        pinchZoomStart.current = zoomRef.current;
        isPanning.current = false;
      } else if (e.touches.length === 1 && zoomRef.current > 1) {
        e.preventDefault();
        isPanning.current = true;
        panStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        lastPan.current = { ...panRef.current };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      e.stopPropagation();
      if (isInsideControls(e.target)) return;
      e.preventDefault();
      if (e.touches.length === 2 && initialPinchDist.current !== null) {
        const dist = getTouchDist(e.touches);
        const ratio = dist / initialPinchDist.current;
        const newZoom = Math.min(Math.max(pinchZoomStart.current * ratio, MIN_ZOOM), MAX_ZOOM);
        setZoom(newZoom);
        if (newZoom <= 1) setPan({ x: 0, y: 0 });
      } else if (e.touches.length === 1 && isPanning.current && zoomRef.current > 1) {
        const dx = e.touches[0].clientX - panStart.current.x;
        const dy = e.touches[0].clientY - panStart.current.y;
        const rawX = lastPan.current.x + dx;
        const rawY = lastPan.current.y + dy;
        setPan(clampPan(rawX, rawY, zoomRef.current));
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      e.stopPropagation();
      if (e.touches.length < 2) initialPinchDist.current = null;
      if (e.touches.length === 0) isPanning.current = false;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [open]);

  // Tap vs swipe on thumbnail (react-slick compatibility)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);
    const dt = Date.now() - touchStartRef.current.time;
    if (dx < 15 && dy < 15 && dt < 300) {
      setOpen(true);
    }
    touchStartRef.current = null;
  }, []);

  // Mouse drag for desktop pan
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom <= 1) return;
    e.preventDefault();
    isPanning.current = true;
    panStart.current = { x: e.clientX, y: e.clientY };
    lastPan.current = { ...panRef.current };

    const onMouseMove = (ev: MouseEvent) => {
      if (!isPanning.current) return;
      const dx = ev.clientX - panStart.current.x;
      const dy = ev.clientY - panStart.current.y;
      setPan(clampPan(lastPan.current.x + dx, lastPan.current.y + dy, zoomRef.current));
    };

    const onMouseUp = () => {
      isPanning.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [zoom]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom(z => {
      const next = Math.min(Math.max(z + delta, MIN_ZOOM), MAX_ZOOM);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (zoom > 1) {
      resetZoom();
    } else {
      setZoom(2.5);
    }
  }, [zoom, resetZoom]);

  const overlay = open ? createPortal(
    <AnimatePresence>
      <motion.div
        ref={overlayRef}
        className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/85 backdrop-blur-sm"
        style={{ pointerEvents: "auto", touchAction: "none" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={close}
        onWheel={handleWheel}
      >
        {/* Controls - above everything */}
        <div
          ref={controlsRef}
          className="absolute top-4 right-4 flex items-center gap-3 z-[10001]"
          onClick={e => e.stopPropagation()}
        >
          <button
            className="text-white active:text-gray-300 transition-colors bg-black/50 rounded-full p-2.5"
            onClick={zoomIn}
            aria-label="Zoom in"
            disabled={zoom >= MAX_ZOOM}
          >
            <ZoomIn size={24} />
          </button>
          <button
            className="text-white active:text-gray-300 transition-colors bg-black/50 rounded-full p-2.5"
            onClick={zoomOut}
            aria-label="Zoom out"
            disabled={zoom <= MIN_ZOOM}
          >
            <ZoomOut size={24} />
          </button>
          <button
            className="text-white active:text-gray-300 transition-colors bg-black/50 rounded-full p-2.5"
            onClick={resetZoom}
            aria-label="Reset zoom"
          >
            <RotateCcw size={24} />
          </button>
          <button
            className="text-white active:text-gray-300 transition-colors bg-black/50 rounded-full p-2.5"
            onClick={close}
            aria-label="Chiudi"
          >
            <X size={24} />
          </button>
        </div>

        {/* Zoom indicator */}
        {zoom > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1 rounded-full z-[10001]">
            {Math.round(zoom * 100)}%
          </div>
        )}

        {/* Image */}
        <motion.div
          className="relative z-[10000] overflow-hidden flex items-center justify-center"
          style={{ width: "90vw", height: "90vh" }}
          onClick={e => e.stopPropagation()}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.85, opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <img
            src={src}
            alt={alt}
            className={`max-h-[90vh] max-w-[90vw] object-contain select-none ${fullscreenClassName ?? ""}`}
            style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              cursor: zoom > 1 ? (isPanning.current ? "grabbing" : "grab") : "zoom-in",
              transition: isPanning.current ? "none" : "transform 0.2s ease",
            }}
            draggable={false}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  ) : null;

  const Theme = light ? LightSkeletonTheme : DarkSkeletonTheme;

  return (
    <>
      <Theme>
        <span
          className={cn(
            "relative block overflow-hidden cursor-pointer",
            className,
          )}
          role="button"
          tabIndex={0}
          aria-label={alt ? `Apri immagine: ${alt}` : "Apri immagine"}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen(true);
            }
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {!loaded && (
            <Skeleton
              className="!absolute !inset-0 !leading-none !h-full !w-full"
              containerClassName="absolute inset-0 block h-full w-full leading-none"
            />
          )}
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            width={width}
            height={height}
            loading={loading}
            className={cn(
              "block w-full h-auto transition-opacity duration-200",
              className?.includes("object-contain")
                ? "object-contain"
                : className?.includes("object-cover")
                  ? "object-cover h-full"
                  : "object-contain",
              loaded ? "opacity-100" : "opacity-0",
            )}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
            {...rest}
          />
        </span>
      </Theme>
      {overlay}
    </>
  );
};

export default ClickableImage;
